import digitalData from 'digital-data';
import { getState, setState } from '../analytics-state';
import { PRIMARY_FILTER } from '../constants';
import { getFilters } from '../thd-analytics-utils';

export const getPageInfoFilters = (arr) => {
  let appliedFilters = [];
  arr.forEach((item, _index) => {
    if (Array.isArray(item)) {
      item = item.map(({ filterType, ...rest }) => ({ ...rest }));
      appliedFilters.push(item);
    }
  });
  return appliedFilters;
};

/**
 * Returns an analytics filter object for the given filter selection.
 * @param {string} dimensionName - The name of the filter dimension.
 * @param {string} label - The label or value of the filter.
 * @param {string} filterType - The type of the filter.
 * @param {Object} analytics - Additional analytics data (optional).
 * @returns {Object} The analytics filter object.
 */
const buildAnalyticsFilterObject = (dimensionName, label, filterType, analytics) => {
  return {
    filterType,
    type: dimensionName,
    value: analytics?.value ? analytics.value : label,
    ...(analytics?.nearbyStore && { nearbyStore: analytics.nearbyStore }),
    ...(analytics?.speed && { speed: analytics.speed }),
    ...(analytics?.freeDelivery && { freeDelivery: analytics.freeDelivery })
  };
};

export const processFilters = (newLayout, filterType) => {
  const state = getState();
  const { pageType, currentPage, sortBy } = state || {};
  let finalSelectedFilters = [];
  digitalData.event = digitalData.event || [];
  digitalData.page = digitalData.page || [];

  // Setting pageTransitionEvent as PRIMARY_FILTER, ReInitAnalytics won't trigger
  setState({
    ...getState(),
    pageTransitionEvent: PRIMARY_FILTER
  });

  // Find all pre-selected filters, if present
  let lastevent = digitalData.event && digitalData.event.length
    ? digitalData.event[digitalData.event.findLastIndex((item) => item.hasOwnProperty('filter'))]
    : { filter: state.filters };

  // Get the current filters for this invocation
  let currentSelectionFilters = [];

  // Check if Clear All btn is clicked

  /**
   * Possible shapes of latestCompleteState (filter history snapshots):
   *
   * 1. Empty state (after "Clear All"):
   *    [[]]
   *    - Single empty array indicating no filters selected
   *
   * 2. Single filter group with one filter:
   *    [
   *      [{ type: "Brand", value: "DeWalt", filterType: "primary" }]
   *    ]
   *
   * 3. Single filter group with multiple filters:
   *    [
   *      [
   *        { type: "Brand", value: "DeWalt", filterType: "primary" },
   *        { type: "Brand", value: "Milwaukee", filterType: "primary" }
   *      ]
   *    ]
   *
   * 4. Multiple filter groups:
   *    [
   *      [
   *        { type: "Brand", value: "DeWalt", filterType: "primary" },
   *        { type: "Brand", value: "Milwaukee", filterType: "primary" }
   *      ],
   *      [
   *        { type: "Price", value: "$0-$50", filterType: "secondary" }
   *      ]
   *    ]
   *
   * Structure:
   * - Outer array: represents different filter groups (Brand, Price, etc.)
   * - Inner arrays: contain individual filter selections within each group
   * - Filter object: { type, value, filterType }
   */
  if (!newLayout?.output?.clear) {
    if (lastevent && lastevent.filter) {
      // Get the latest filter state (most recent complete state like the last index)
      let latestCompleteState = Array.isArray(lastevent.filter[0]) && Array.isArray(lastevent.filter[0][0])
        ? lastevent.filter[lastevent.filter.length - 1]
        : lastevent.filter;

      /**
       * The check prevents copying the empty state [[]] into currentSelectionFilters,
       * ensuring we start fresh when all filters have been cleared.

       - latestCompleteState can be [[]] after a "Clear All" action
       - We don't want to copy [[]] as our starting point
       - We only want to preserve actual filter selections from previous state
       */
      if (Array.isArray(latestCompleteState) && !(latestCompleteState.length === 1 && latestCompleteState[0].length === 0)) {
        currentSelectionFilters = [...latestCompleteState];
      }

      // Process new filters
      newLayout?.output?.forEach((filterIndex = {}, indexf) => {
        // Remove filter groups that are not present in newLayout
        if (newLayout?.output?.length > 0) {
          const newLayoutTypes = newLayout.output.map(filter => filter.label);
          currentSelectionFilters = currentSelectionFilters.filter(group => {
            if (Array.isArray(group) && group.length > 0) {
              return newLayoutTypes.some(type => type?.toUpperCase() === group[0]?.type?.toUpperCase());
            }
            return [];
          });
        }
        const selectedFilters = [];
        newLayout?.output?.[indexf]?.refinements?.forEach((crumb = {}, index) => {
          let _rowItem;

          // Search in current selection first
          currentSelectionFilters.forEach((arr, i) => {
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                if (item.value === crumb?.label) {
                  _rowItem = item;
                }
              });
            } else if (arr?.value === crumb?.label) {
              _rowItem = arr;
            }
          });

          if (_rowItem) {
            selectedFilters.push(_rowItem);
          } else {
            const analyticsData = crumb?.analytics || newLayout?.output?.[indexf]?.analytics;
            const analyticsObj = buildAnalyticsFilterObject(newLayout?.output?.[indexf].label, crumb?.label, crumb?.filterType || filterType, analyticsData);
            selectedFilters.push(analyticsObj);
          }
        });

        // Update or add the filter group
        const existingGroupIndex = currentSelectionFilters.findIndex(group =>
          Array.isArray(group) && group.length > 0 &&
          group[0]?.type?.toUpperCase() === newLayout?.output?.[indexf]?.label?.toUpperCase()
        );

        if (existingGroupIndex !== -1) {
          const existingGroup = currentSelectionFilters[existingGroupIndex];
          const updatedGroup = [];

          // First, preserve existing filters that are still selected
          existingGroup.forEach(existingFilter => {
            if (existingFilter?.type?.toUpperCase()!== 'GET IT FAST' && selectedFilters.some(newFilter => newFilter?.value?.toUpperCase() === existingFilter?.value?.toUpperCase())) {
              // Keep the original filter object to preserve its filterType
              updatedGroup.push(existingFilter);
            }
          });

          // Then, add new filters that weren't in the existing group
          selectedFilters.forEach(newFilter => {
            if (!updatedGroup.some(uf => uf?.value?.toUpperCase() === newFilter?.value?.toUpperCase())) {
              updatedGroup.push(newFilter);
            }
          });

          currentSelectionFilters[existingGroupIndex] = updatedGroup;
        } else {
          currentSelectionFilters.push(selectedFilters);
        }
      });
    } else {
      // If first filter selection is done
      newLayout?.output?.forEach((filterIndex, indexf) => {
        const selectedFilters = [];
        newLayout?.output?.[indexf]?.refinements.forEach((crumb, index) => {
          if (!crumb?.label) return;
          const analyticsData = crumb?.analytics || newLayout?.output?.[indexf]?.analytics;
          const analyticsObj = buildAnalyticsFilterObject(newLayout?.output?.[indexf].label, crumb?.label, crumb?.filterType || filterType, analyticsData);
          selectedFilters.push(analyticsObj);
        });
        currentSelectionFilters.push(selectedFilters);
      });
    }
  }

  // Build the historical structure if history ALready Exists
  if (lastevent && lastevent.filter && Array.isArray(lastevent.filter[0]) && Array.isArray(lastevent.filter[0][0])) {
    // Previous history exists????, add current state
    if (newLayout?.output?.clear) {
      finalSelectedFilters = [...lastevent.filter, [[]]];
    } else {
      finalSelectedFilters = [...lastevent.filter, currentSelectionFilters];
    }
  } else if (lastevent && lastevent.filter && lastevent.filter.length > 0) {
    // First time creating history, so wrapping existing filters and add current
    finalSelectedFilters = [lastevent.filter, currentSelectionFilters];
  } else {
    // No previous filters, start new history
    finalSelectedFilters = [currentSelectionFilters];
  }

  digitalData.event.push({
    category: { primaryCategory: pageType || '' },
    eventInfo: { eventName: 'filter By' },
    displayAttributes: {
      pageNumber: currentPage || 0,
      productsAvailability: 'all products',
      sortBy: sortBy || 'bestmatch',
      viewType: 'grid'
    },
    filter: finalSelectedFilters
  });

  digitalData.page.filter = finalSelectedFilters.length && finalSelectedFilters[finalSelectedFilters.length - 1].length
    ? getPageInfoFilters(finalSelectedFilters[finalSelectedFilters.length - 1])
    : getFilters(finalSelectedFilters[finalSelectedFilters.length - 1]);

  // update state with addition/removal of filters
  setState({
    ...getState(),
    filters: finalSelectedFilters
  });
};


/* Format and arrange the refinements passed down
 into new data structure i.e Arrays of arrays */
export const transformArray = (input) => {
  const result = [];

  // A helper object to group items by dimension
  const groups = {};

  // Process each array in the input
  input.forEach((subArray) => {
    if (Array.isArray(subArray)) {
      subArray.forEach((item) => {
        const dimensionName = item.type || item.dimensionName;
        const label = item.value || item.label;
        const analytics = item.analytics || {};
        if (!groups[dimensionName]) {
          groups[dimensionName] = [];
        }

        groups[dimensionName].push({ dimensionName, label, analytics });
      });
    }
  });

  // Convert grouped items into the required output format
  for (const [dimensionName, items] of Object.entries(groups)) {
    result.push({
      label: dimensionName,
      refinements: items
    });
  }

  return result;
};

const addFilterToGroup = (filters, group, output) => {
  if (group && Array.isArray(group)) {
    // Case 1: group is an array - add to existing array
    group.push({ ...output });
  } else if (group) {
    // Case 2: group is a single object - convert to array and replace
    const groupIndex = filters.indexOf(group);
    filters[groupIndex] = [group, { ...output }];
  } else {
    // Case 3: no group found - create new group
    filters.push([{ ...output }]);
  }
};

const removeFilterFromGroup = (filters, group, label) => {
  if (Array.isArray(group)) {
    const filterIndex = group.findIndex((filter) => filter.label === label);

    if (filterIndex !== -1) {
      group.splice(filterIndex, 1);
    }
  }

  if (Array.isArray(group) && !group.length) {
    filters.splice(filters.indexOf(group), 1);
  }
};

export const updateFilters = (output, filters = []) => {
  const { dimensionName, label, selected } = output;

  // Find the group that matches the output's dimensionName
  const group = filters.find((group) => {
    if (Array.isArray(group)) {
      return group.some((filter) => filter.type === dimensionName || filter.dimensionName === dimensionName);
    } else if (group.type || group.dimensionName) {
      return group.type === dimensionName || group.dimensionName === dimensionName;
    }
  });
  if (selected === null) {
    // Add the filter to the group or create a new group if it doesn't exist
    // for, Get It Fast no previous GIF group anymore, each time its radio selected.
    if (output.dimensionName.toUpperCase() === 'GET IT FAST') {
      // Remove existing "Get It Fast" filter (radio button behavior)
      filters = filters.filter(group => {
        if (!Array.isArray(group)) return true;
        return group[0]?.type?.toUpperCase() !== 'GET IT FAST';
      });

      addFilterToGroup(filters, undefined, output)
    } else {
      addFilterToGroup(filters, group, output);
    }
  } else if (group) {
    // Remove the filter from the group
    removeFilterFromGroup(filters, group, label);
  }

  return filters;
};

export const formatFilters = (newLayout) => {
  let { filters = [] } = getState();
  let { output = {} } = newLayout;

  if (output?.clear) {
    newLayout.output = transformArray([]);
  } else if (output?.dimensionName) {
    const lastFilterState = filters.length ? [...filters[filters.length - 1]] : [];
    const updatedFilters = updateFilters(output, lastFilterState);
    newLayout.output = transformArray(updatedFilters);
  }
  const filterType = output.analytics ? output.analytics.filterType : 'others';
  processFilters(newLayout, filterType);
};

export const primaryFilters = () => {
  // when user selects from primary filters
  window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-filters-refinements', (newLayout = {}) => {
    processFilters(newLayout, 'primary');
  });

  // when user selects from secondary filters
  window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-filters-refinements-secondary', (newLayout = {}) => {
    processFilters(newLayout, 'secondary');
  });

  // when user removes any one filters from chips
  window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-filters-refinements-shortings', (newLayout = {}) => {
    processFilters(newLayout, 'secondary');
  });

  // when user clicks on clear all (removes all filters from chips)
  window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-filters-refinements-clear-all', (newLayout = {}) => {
    processFilters(newLayout, 'clearAllFilters');
  });

  // when user selects from visual-Nav (getItFast filters)
  window.LIFE_CYCLE_EVENT_BUS.on('filters-refinements-visualNav.click', (newLayout = {}) => {
    formatFilters(newLayout);
  });
};
